import { FormEvent, useEffect, useMemo, useState } from 'react';
import { supabase, waLink, IS_SUPABASE_CONFIGURED } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { TIPOS_CLIENTE } from '@/lib/constants';

interface ProdOption {
  value: string;     // identificador del producto enviado
  name: string;      // display name (ALL-CAPS)
  cat: string;       // categoría
  qtyKey: string;    // key local para la cantidad
}

const PRODS: ProdOption[] = [
  { value: 'Espaldilla de Cerdo',         name: 'ESPALDILLA',      cat: 'Embutidos',          qtyKey: 'espaldilla' },
  { value: 'El Dorado (Jamón de Pierna)', name: 'EL DORADO',       cat: 'Piernas y Jamones',  qtyKey: 'dorado' },
  { value: 'Chorizo Especial',            name: 'CHORIZO',         cat: 'Chorizos',           qtyKey: 'chorizo' },
  { value: 'Pierna Española Ahumada',     name: 'PIERNA ESPAÑOLA', cat: 'Piernas y Jamones',  qtyKey: 'pierna' },
  { value: 'Tocino Ahumado Premium',      name: 'TOCINO',          cat: 'Tocinos',            qtyKey: 'tocino' },
  { value: 'Longaniza de Cerdo',          name: 'LONGANIZA',       cat: 'Chorizos',           qtyKey: 'longaniza' },
];

interface SelectedItem {
  value: string;
  kg: number | '';
}

/**
 * Clasificación automática: derivada del total de kg + tipo de cliente, según el
 * comportamiento del HTML de referencia.
 */
function classify(totalKg: number, tipo: string) {
  const badges: Array<{ cls: string; text: string }> = [];
  if (totalKg >= 100)      badges.push({ cls: 'badge-alta',    text: 'ALTA' });
  else if (totalKg >= 50)  badges.push({ cls: 'badge-media',   text: 'MEDIA' });
  else                     badges.push({ cls: 'badge-baja',    text: 'BAJA' });
  if (tipo === 'Distribuidor / Mayorista') badges.push({ cls: 'badge-dist',    text: 'DISTRIBUIDOR' });
  else if (tipo === 'Restaurante / Taquería') badges.push({ cls: 'badge-horeca', text: 'HORECA' });
  else if (tipo === 'Carnicería')            badges.push({ cls: 'badge-retail', text: 'RETAIL' });
  else if (tipo === 'Cliente final')         badges.push({ cls: 'badge-directo', text: 'DIRECTO' });
  return badges;
}

export function InlineQuoteForm() {
  const { user, profile } = useAuth();
  const [selected, setSelected] = useState<SelectedItem[]>([]);
  const [nombre, setNombre] = useState(profile?.name ?? '');
  const [email, setEmail] = useState(profile?.email ?? '');
  const [tel, setTel] = useState(profile?.tel ?? '');
  const [tipo, setTipo] = useState('');
  const [notas, setNotas] = useState('');
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [errs, setErrs] = useState<{ nombre?: string; tel?: string; email?: string }>({});
  const [busy, setBusy] = useState(false);
  const [classified, setClassified] = useState(false);

  useEffect(() => {
    if (profile) {
      setNombre((n) => n || profile.name);
      setEmail((e) => e || profile.email);
      setTel((t) => t || profile.tel || '');
    }
  }, [profile]);

  const toggle = (p: ProdOption) => {
    setSelected((sel) => {
      if (sel.some((s) => s.value === p.value)) {
        return sel.filter((s) => s.value !== p.value);
      }
      return [...sel, { value: p.value, kg: '' }];
    });
  };

  const setKg = (value: string, kg: string) => {
    setSelected((sel) =>
      sel.map((s) => (s.value === value ? { ...s, kg: kg === '' ? '' : Math.max(0, Number(kg)) } : s)),
    );
  };

  const totalKg = useMemo(
    () => selected.reduce((sum, s) => sum + (typeof s.kg === 'number' ? s.kg : 0), 0),
    [selected],
  );

  const badges = useMemo(() => classify(totalKg, tipo), [totalKg, tipo]);

  function validate(): boolean {
    const e: typeof errs = {};
    if (!nombre.trim()) e.nombre = 'Requerido';
    if (!tel.trim() || tel.replace(/\D/g, '').length < 8) e.tel = 'Teléfono inválido';
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = 'Correo inválido';
    setErrs(e);
    return Object.keys(e).length === 0;
  }

  const buildWaMsg = () => {
    const snapshot = selected
      .filter((s) => typeof s.kg === 'number' && s.kg > 0)
      .map((s) => `${s.value} (${s.kg} kg)`)
      .join(', ');
    return (
      `Hola Las Polvillas, solicito cotización.\n\n*Cliente:* ${nombre}\n*Tel:* ${tel}` +
      (email ? `\n*Correo:* ${email}` : '') +
      (tipo ? `\n*Tipo:* ${tipo}` : '') +
      `\n*Productos:* ${snapshot || '(sin especificar)'}\n*Total:* ${totalKg} kg` +
      (notas ? `\n\n*Notas:* ${notas}` : '')
    );
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setMsg(null);
    if (selected.length === 0) {
      setMsg({ kind: 'err', text: 'Selecciona al menos un producto.' });
      return;
    }
    if (!validate()) return;

    setBusy(true);
    setClassified(true);

    if (!IS_SUPABASE_CONFIGURED) {
      window.open(waLink(buildWaMsg()), '_blank', 'noopener');
      setMsg({ kind: 'ok', text: '¡Abrimos WhatsApp con tu cotización!' });
      setBusy(false);
      return;
    }

    const prioridad = totalKg >= 100 ? 'ALTA' : totalKg >= 50 ? 'MEDIA' : 'BAJA';

    const { data: quote, error } = await supabase
      .from('quotes')
      .insert({
        client_id: user?.id ?? null,
        guest_email: user ? null : email.trim() || null,
        guest_name:  user ? null : nombre.trim(),
        guest_tel:   user ? null : tel.trim(),
        tipo_cliente: tipo || null,
        total_kg: totalKg,
        prioridad,
        status: 'pendiente',
        notas: notas.trim() || null,
      })
      .select()
      .single();

    if (error || !quote) {
      setBusy(false);
      setMsg({ kind: 'err', text: 'No pudimos enviar la cotización. Intenta de nuevo.' });
      return;
    }

    const items = selected
      .filter((s) => typeof s.kg === 'number' && s.kg > 0)
      .map((s) => ({
        quote_id: quote.id,
        product_id: s.value,
        product_name: s.value,
        kg: s.kg as number,
      }));
    if (items.length > 0) await supabase.from('quote_items').insert(items);

    window.open(waLink(buildWaMsg()), '_blank', 'noopener');
    setMsg({ kind: 'ok', text: '¡Cotización enviada! Abrimos WhatsApp con el resumen.' });
    setBusy(false);
  };

  const onSaveOnly = async () => {
    if (selected.length === 0) {
      setMsg({ kind: 'err', text: 'Selecciona al menos un producto.' });
      return;
    }
    if (!validate()) return;
    setClassified(true);
    setMsg({ kind: 'ok', text: '¡Solicitud guardada! Te contactamos en menos de 24 hrs.' });
  };

  return (
    <section id="cotizacion">
      <div className="wrap">
        <div className="cotizacion-hd reveal">
          <span className="eyebrow">Solicita tu cotización</span>
          <h2 className="st">COTIZAR PRODUCTOS</h2>
        </div>

        <div className="qform-wrap reveal">
          <p className="qform-sub">
            Selecciona productos, indica cantidades y te contactamos en menos de 24 hrs.
          </p>

          <form id="quoteForm" onSubmit={onSubmit} noValidate>
            <div className="fg"><label>PRODUCTOS DE INTERÉS</label></div>

            <div className="prods-check">
              {PRODS.map((p) => {
                const sel = selected.find((s) => s.value === p.value);
                return (
                  <label key={p.value} className="prod-check-item">
                    <input
                      type="checkbox"
                      name="prod"
                      value={p.value}
                      checked={!!sel}
                      onChange={() => toggle(p)}
                    />
                    <div className="pc-name">{p.name}</div>
                    <div className="pc-cat">{p.cat}</div>
                    <div className="qty-row">
                      <input
                        type="number"
                        name={`qty_${p.qtyKey}`}
                        min={1}
                        placeholder="kg"
                        value={sel?.kg ?? ''}
                        disabled={!sel}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setKg(p.value, e.target.value)}
                      />
                      <span style={{ fontSize: '.75rem', color: 'var(--muted)' }}>kg</span>
                    </div>
                  </label>
                );
              })}
            </div>

            <div className="f-row">
              <div className="fg" style={{ margin: 0 }}>
                <label>NOMBRE O EMPRESA</label>
                <input
                  type="text"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  placeholder="Tu nombre o razón social"
                  className={errs.nombre ? 'err' : ''}
                />
                <div className="field-err">{errs.nombre}</div>
              </div>
              <div className="fg" style={{ margin: 0 }}>
                <label>TELÉFONO / WHATSAPP</label>
                <input
                  type="tel"
                  value={tel}
                  onChange={(e) => setTel(e.target.value)}
                  placeholder="56 5371-2507"
                  className={errs.tel ? 'err' : ''}
                />
                <div className="field-err">{errs.tel}</div>
              </div>
            </div>

            <div className="f-row" style={{ marginTop: 18 }}>
              <div className="fg" style={{ margin: 0 }}>
                <label>TIPO DE CLIENTE</label>
                <select value={tipo} onChange={(e) => setTipo(e.target.value)}>
                  <option value="">Seleccionar...</option>
                  {TIPOS_CLIENTE.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="fg" style={{ margin: 0 }}>
                <label>CORREO (OPCIONAL)</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@correo.com"
                  className={errs.email ? 'err' : ''}
                />
                <div className="field-err">{errs.email}</div>
              </div>
            </div>

            <div className="fg" style={{ marginTop: 18 }}>
              <label>NOTAS / FRECUENCIA DE PEDIDO</label>
              <textarea
                value={notas}
                onChange={(e) => setNotas(e.target.value)}
                placeholder="Frecuencia de pedido, presentación preferida, zona de entrega..."
              />
            </div>

            <div className={`quote-classified ${classified ? 'show' : ''}`}>
              <div className="qc-label">CLASIFICACIÓN AUTOMÁTICA</div>
              <div className="qc-badges">
                {badges.map((b) => (
                  <span key={b.text} className={`hi-badge ${b.cls}`}>{b.text}</span>
                ))}
              </div>
            </div>

            <div className="qform-actions">
              <button type="submit" className="btn-wa" disabled={busy}>
                <i className="fab fa-whatsapp" /> {busy ? 'ENVIANDO…' : 'ENVIAR POR WHATSAPP'}
              </button>
              <button type="button" className="btn btn-fill" onClick={onSaveOnly} disabled={busy}>
                GUARDAR SOLICITUD
              </button>
            </div>

            <p style={{
              textAlign: 'center',
              marginTop: 16,
              fontSize: '.82rem',
              minHeight: 20,
              color: msg?.kind === 'err' ? '#ff6b6b' : '#4caf50',
            }}>
              {msg?.text ?? ''}
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}
